import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * Ordered route-permission pairs for redirect when user lacks permission.
 * First path user can access is used as fallback.
 */
/** Paths used as fallbacks when the current route is not allowed — must cover every PermissionGuard in App.jsx. */
const ROUTE_PERMISSIONS = [
  { path: '/', permission: 'dashboard' },
  { path: '/trending-events', permission: 'dashboard' },
  { path: '/content', permission: 'content' },
  { path: '/site-labels', permission: 'content' },
  { path: '/highlighted-events', permission: 'highlighted_events' },
  { path: '/home-categories', permission: 'home_categories' },
  { path: '/home-category-order', permission: 'home_category_order' },
  { path: '/expedition', permission: 'expedition' },
  { path: '/expedition-page', permission: 'expedition_page' },
  { path: '/careers', permission: 'careers' },
  { path: '/applicants', permission: 'applicants' },
  { path: '/lead-requests', permission: 'lead_requests' },
  { path: '/lead-reports', permission: 'lead_reports' },
  { path: '/whatsapp-automation', permission: 'whatsapp_automation' },
  { path: '/short-links', permission: 'short_links' },
  { path: '/support-tickets', permission: 'lead_requests' },
  { path: '/create-lead', permission: 'lead_requests_create' },
  { path: '/callback-requests', permission: 'lead_requests' },
  { path: '/volunteer-inquiries', permission: 'volunteer_inquiries' },
  { path: '/pdf-requests', permission: 'lead_requests' },
  { path: '/campsites', permission: 'categories' },
  { path: '/categories', permission: 'categories' },
  { path: '/event-types', permission: 'categories' },
  { path: '/locations', permission: 'categories' },
  { path: '/treks', permission: 'treks' },
  { path: '/attractions', permission: 'attractions' },
  { path: '/add-ons', permission: 'add_ons' },
  { path: '/event-batches', permission: 'event_batches' },
  { path: '/registrations', permission: 'registrations' },
  { path: '/tickets', permission: 'tickets' },
  { path: '/receipts', permission: 'receipts' },
  { path: '/manual-receipts', permission: 'receipts' },
  { path: '/cancellations', permission: 'cancellations' },
  { path: '/special-events', permission: 'special_events' },
  { path: '/contact', permission: 'contact' },
  { path: '/about', permission: 'about' },
  { path: '/team', permission: 'team' },
  { path: '/users', permission: 'users' },
  { path: '/role-builder', permission: 'users' },
  { path: '/activity-logs', permission: 'activity_logs' }
];

/**
 * Wraps admin pages so only users with the required permission can view.
 * Super Admin has full access. Admin must have the permission.
 * If no permission, redirects to first allowed route or /.
 */
function NoModuleAccess() {
  return (
    <div
      className="module-page"
      style={{
        padding: '2rem',
        maxWidth: 560,
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        color: '#0f172a'
      }}
    >
      <h1 className="module-title" style={{ fontSize: '1.25rem', color: '#0f172a' }}>
        No admin module access
      </h1>
      <p style={{ color: '#334155', lineHeight: 1.6 }}>
        This account is signed in but does not have permission for any screen in the panel (or the fallback list is out of date). Ask a super admin to assign a role with at least one module, or log out and use an account with access.
      </p>
    </div>
  );
}

export default function PermissionGuard({ permission, children }) {
  const { can } = useAuth();
  const location = useLocation();

  if (can(permission)) {
    return children;
  }

  const firstAllowed = ROUTE_PERMISSIONS.find((r) => can(r.permission));
  if (!firstAllowed) {
    return <NoModuleAccess />;
  }
  if (firstAllowed.path === location.pathname) {
    return <NoModuleAccess />;
  }
  return <Navigate to={firstAllowed.path} replace state={{ from: location.pathname }} />;
}
